import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import { PAST_EVENTS_LIMIT } from 'shared/constants';
import * as roomActions from 'features/room-builder/duck';
import { Accordion, AlertNotice, ModalInfo, Select, Spinner } from 'shared/components';
import InfiniteScroll from 'react-infinite-scroll-component';
import CopyRoomCollapsibleTitle from './CopyRoomCollapsibleTitle';
import './CopyRooms.scss';

const mapOptions = (locationsList, locEvPairs, currentSourceId, t) => {
  const options = [
    {
      value: 'empty',
      label: t('roomBuilder.doNotCopyRooms'),
    },
  ];

  //set as options only unused locations
  locationsList.forEach((loc) => {
    const alreadyUsedLocation = Object.entries(locEvPairs).find(
      ([sourceLocEvId, destLocEvId]) =>
        +sourceLocEvId !== +currentSourceId && loc.locationEvent.id === +destLocEvId
    );

    options.push({
      value: loc.locationEvent.id,
      label: loc.name,
      disabled: !!alreadyUsedLocation,
    });
  });
  return options;
};

class CopyRooms extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedEvent: null,
      hasMoreItems: !!props.pastEvents.length && props.pastEvents.length % PAST_EVENTS_LIMIT === 0,
      locEvPairs: {},
      errors: {},
      generalErrorMessage: '',
    };

    this.canBeCopied = this.canBeCopied.bind(this);
    this.selectEvent = this.selectEvent.bind(this);
    this.resetSelectedEvent = this.resetSelectedEvent.bind(this);
    this.onChangeLocation = this.onChangeLocation.bind(this);
    this.loadItems = this.loadItems.bind(this);
    this.onCopyRooms = this.onCopyRooms.bind(this);
    this.closeModal = this.closeModal.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (
      !prevProps.pastEvents.length &&
      this.props.pastEvents.length &&
      this.props.pastEvents.length % PAST_EVENTS_LIMIT === 0
    ) {
      this.setState({
        hasMoreItems: true,
      });
    }
  }

  componentWillUnmount() {
    this.props.actions.clearPastEventsAction();
  }

  canBeCopied(locationEvents) {
    return !isEmpty(filter(locationEvents, (o) => o.location.canBeCopied === true));
  }

  selectEvent(eventId) {
    const { pastEvents, locations } = this.props;
    const selectedEvent = pastEvents.find((ev) => ev.id === +eventId);
    const filteredLocationEvents = selectedEvent.locationEvents.filter(
      (locEv) => locEv.location.canBeCopied
    ); //filter out locations with canBeCopied = false
    let matchingLocations = {};

    //preselect matching locations
    filteredLocationEvents.forEach((locEv) => {
      let matchLoc = locations.find(
        (loc) => loc.name === locEv.location.name && loc.area === locEv.location.area
      );
      if (matchLoc) {
        matchingLocations[locEv.id] = matchLoc.locationEvent.id;
      }
    });

    this.setState({
      selectedEvent: {
        ...selectedEvent,
        locationEvents: filteredLocationEvents,
      },
      locEvPairs: matchingLocations,
      generalErrorMessage: '',
    });
  }

  resetSelectedEvent() {
    this.setState({
      selectedEvent: {},
      errors: {},
      locEvPairs: {},
      generalErrorMessage: '',
    });
  }

  onChangeLocation(e, sourceLocEv) {
    const { value } = e.target;

    this.setState((prevState) => ({
      locEvPairs: {
        ...prevState.locEvPairs,
        [sourceLocEv]: value,
      },
      errors: {
        ...prevState.errors,
        [sourceLocEv]: '',
      },
      generalErrorMessage: '',
    }));
  }

  async onCopyRooms() {
    const { selectedEvent, locEvPairs } = this.state;
    let flagError = false;
    let noLocationSelected = true;

    selectedEvent.locationEvents.forEach((locEv) => {
      if (!locEvPairs[locEv.id]) {
        this.setState((prevState) => ({
          errors: {
            ...prevState.errors,
            [locEv.id]: 'errors.selectionRequired',
          },
        }));
        flagError = true;
      } else if (locEvPairs[locEv.id] !== 'empty') {
        //check if some location is selected
        noLocationSelected = false;
      }
    });

    if (!flagError) {
      if (noLocationSelected) {
        this.setState({
          generalErrorMessage: 'roomBuilder.noLocationError',
        });
        return;
      }

      await this.props.copyRooms({
        sourceTceId: selectedEvent.id,
        locationEvents: Object.entries(locEvPairs)
          .filter(([sourceId, destId]) => destId !== 'empty')
          .map(([sourceId, destId]) => ({
            sourceLocationEventId: sourceId,
            destLocationEventId: destId,
          })),
      });

      if (this.props.errorCopyRooms) {
        this.setState({
          generalErrorMessage: 'errors.somethingWentWrong',
        });
      }
    }
  }

  loadItems() {
    const { tcCycleId, currentEvent, pastEvents } = this.props;
    this.props.actions
      .getPastEvents(tcCycleId, currentEvent.id, {
        limit: PAST_EVENTS_LIMIT,
        skip: pastEvents.length,
      })
      .then(() => {
        if (this.props.pastEvents.length % PAST_EVENTS_LIMIT !== 0 || this.props.noMorePastEvents) {
          this.setState({
            hasMoreItems: false,
          });
        }
      });
  }

  closeModal() {
    this.setState({
      selectedEvent: null,
    });
    this.props.closeModal();
  }
  render() {
    const { selectedEvent, hasMoreItems, locEvPairs, errors, generalErrorMessage } = this.state;
    const { copyRoomsOpen, locations, currentEvent, pastEvents } = this.props;
    const loader = <Spinner />;

    return (
      <React.Fragment>
        <ModalInfo
          modalIsOpen={copyRoomsOpen}
          closeModal={this.closeModal}
          headerTitle={this.props.t('roomBuilder.copyRoomsLabel')}
          onSubmit={this.onCopyRooms}
          hideButtons={isEmpty(selectedEvent)}
          className="copy-rooms-modal"
          saveLabel={this.props.t('roomBuilder.copyRoomsButton')}
          showCancel
          cancelLabel={this.props.t('common.labels.back')}
          onCancel={this.resetSelectedEvent}
          scrollableContentId="copyRoomsModal">
          {isEmpty(selectedEvent) ? (
            <div>
              <div className="alert alert-primary bg-light-blue border-0">
                <i className="fa fa-info-circle pe-2" />
                <p className="m-0">{this.props.t('roomBuilder.copyRoomsInfo')}</p>
              </div>

              <div className="event-list">
                <InfiniteScroll
                  dataLength={pastEvents.length}
                  next={this.loadItems}
                  hasMore={hasMoreItems}
                  loader={loader}
                  scrollableTarget="copyRoomsModal">
                  <Accordion>
                    {pastEvents.map((pastEvent) => (
                      <div
                        key={`room-${pastEvent.id}`}
                        data-classname={
                          this.canBeCopied(pastEvent.locationEvents) && !pastEvent.studyFlag
                            ? null
                            : 'copy-disabled'
                        }
                        data-trigger={
                          <CopyRoomCollapsibleTitle
                            id={pastEvent.id}
                            canBeCopied={
                              this.canBeCopied(pastEvent.locationEvents) && !pastEvent.studyFlag
                            }
                            testDate={pastEvent.testingEvent.name}
                            selectEvent={this.selectEvent}
                          />
                        }>
                        {pastEvent.locationEvents.map((locationEvent) => (
                          <div
                            key={`event-${locationEvent.id}`}
                            className={
                              locationEvent.location.canBeCopied ? 'mb-3' : 'mb-3 copy-disabled'
                            }>
                            <div className="text-strong">{`${locationEvent.location.name}${
                              locationEvent.location.area &&
                              locationEvent.location.area.length > 0 &&
                              `, ${locationEvent.location.area}`
                            }`}</div>
                            {locationEvent.rooms.length ? (
                              locationEvent.rooms.map((room) => (
                                <div key={`room-${room.id}`}>{`${room.name} (${
                                  room.capacity != null
                                    ? `${room.capacity} ${this.props.t('testCenter.labelSeats')}`
                                    : this.props.t('common.labels.notSpecified')
                                })`}</div>
                              ))
                            ) : (
                              <div>{this.props.t('roomBuilder.noRoomsAvailable')}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </Accordion>
                </InfiniteScroll>
              </div>
            </div>
          ) : (
            <div className="select-location-view">
              <h4 className="">{selectedEvent.testingEvent.name}</h4>
              {selectedEvent.locationEvents.map((locationEvent) => (
                <div key={`event-${locationEvent.id}`} className="location-row border-top-gray">
                  <h5 className="fw-bold pb-2">{`${locationEvent.location.name}${
                    locationEvent.location.area &&
                    locationEvent.location.area.length > 0 &&
                    `, ${locationEvent.location.area}`
                  } (${locationEvent.rooms.length} ${this.props.t('common.labels.rooms')})`}</h5>
                  <Select
                    className="select-destination"
                    options={mapOptions(locations, locEvPairs, locationEvent.id, this.props.t)}
                    placeholder={this.props.t('common.labels.select', { what: 'location' })}
                    name="testCenterEventId"
                    value={locEvPairs[locationEvent.id]}
                    onChange={(e) => this.onChangeLocation(e, locationEvent.id)}
                    label={this.props.t('roomBuilder.chooseLocationLabel')}
                    errors={errors[locationEvent.id]}
                  />
                  {locationEvent.rooms.length ? (
                    locationEvent.rooms.map((room) => (
                      <div key={`room-${room.id}`}>{`${room.name} (${
                        room.capacity != null
                          ? `${room.capacity} ${this.props.t('testCenter.labelSeats')}`
                          : this.props.t('common.labels.notSpecified')
                      })`}</div>
                    ))
                  ) : (
                    <div>{this.props.t('roomBuilder.noRoomsAvailable')}</div>
                  )}
                </div>
              ))}
              {generalErrorMessage && (
                <AlertNotice message={this.props.t(generalErrorMessage)} type="error" />
              )}
            </div>
          )}
        </ModalInfo>
      </React.Fragment>
    );
  }
}

function mapStateToProps(state) {
  return {
    pastEvents: state.roomOptions.pastEvents,
    noMorePastEvents: state.roomOptions.noMorePastEvents,
    errorCopyRooms: state.roomOptions.errorCopyRooms,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...roomActions }, dispatch),
  };
}

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(CopyRooms));
